import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router';
import {
  ArrowLeft, BadgeCheck, TrendingUp, BarChart3,
  Clock, Copy, Info, Layers,
  Eye, EyeOff, Trash2, AlertTriangle,
} from 'lucide-react';
import {
  useCopySignal, useSignalTrades, useCopySubscriptions, useUnsubscribeFromCopy,
  useCopyPrices, useSignalDailyPnl, useSignalPerformance, useSignalCopierStats,
  useUpdateSignal, useDeleteSignal,
  type ProviderTrade, type CopySignal, type CopySubscription, type PeriodPerformance,
} from '@/hooks/use-copy-trade';
import { useAuth } from '@/hooks/use-auth';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { CopyBotDrawer } from '@/components/copy-bot-drawer';
import { formatPnl, pnlColor, formatPrice, getClientContractSize } from '@/lib/format';

function dicebearAvatar(seed: string): string {
  return `https://api.dicebear.com/9.x/shapes/svg?seed=${encodeURIComponent(seed)}&backgroundColor=f97316,fb923c,fbbf24&shape1Color=f97316,ea580c`;
}

type Tab = 'overview' | 'positions' | 'history';
type PerfPeriod = '7D' | '30D' | '90D' | '180D' | '360D' | 'ALL';

export default function CopySignalDetailPage() {
  const { id } = useParams<{ id: string }>();
  // searchParams removed — subscribe modal replaced by /bots/new page
  const { data: signal, isLoading } = useCopySignal(id!);
  const { data: subscriptions } = useCopySubscriptions();
  const { data: livePrices } = useCopyPrices();
  const { data: openTradesData } = useSignalTrades(id!, { status: 'OPEN', limit: 100 });
  const { data: performance } = useSignalPerformance(id!);
  const { data: copierStats } = useSignalCopierStats(id!);
  const { data: dailyPnl } = useSignalDailyPnl(id!, 90);

  const [tab, setTab] = useState<Tab>('overview');
  const [perfPeriod, setPerfPeriod] = useState<PerfPeriod>('30D');
  // Subscribe modal removed — now navigates to /bots/new with signal params

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
  const unsubscribe = useUnsubscribeFromCopy();
  const updateSignal = useUpdateSignal();
  const deleteSignal = useDeleteSignal();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.is_admin === true;

  const activeSub = (subscriptions ?? []).find(s => s.signal?.id === id && s.status !== 'STOPPED' && !s.copierBot?.archived_at);

  if (isLoading) return <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>;
  if (!signal) return <div className="text-center py-16"><p className="text-sm text-ink-3">Signal not found</p></div>;

  const openTrades = openTradesData?.trades ?? [];
  let floatingPnl = 0;
  if (livePrices && openTrades.length > 0) {
    for (const t of openTrades) {
      const price = livePrices[t.symbol];
      if (!price) continue;
      const cp = t.type === 'BUY' ? price.bid : price.ask;
      const diff = t.type === 'BUY' ? cp - t.open_price : t.open_price - cp;
      floatingPnl += diff * t.volume * getClientContractSize(t.symbol, livePrices || undefined);
    }
    floatingPnl = parseFloat(floatingPnl.toFixed(2));
  }
  // realizedPnl = signal.total_pnl, combinedPnl = realizedPnl + floatingPnl
  const avatarUrl = signal.avatar_url || dicebearAvatar(signal.name);
  const symbolDisplay = signal.symbol_filter?.join(', ') || 'All Symbols';
  const currentPerf = performance?.[perfPeriod];

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <Link to="/copy-trade" className="text-xs text-ink-3 hover:text-orange flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> Back to Copy Trade
        </Link>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <img src={avatarUrl} alt="" className="w-12 h-12 rounded-full object-cover bg-surface" />
            <div>
              <h1 className="text-xl font-bold text-ink flex items-center gap-2">
                {signal.name}
                {signal.is_verified && <BadgeCheck size={18} className="text-blue-500" />}
              </h1>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-ink-3">{symbolDisplay}</span>
                {copierStats && (
                  <span className="text-xs text-ink-4">· {copierStats.activeCopiers} copier{copierStats.activeCopiers !== 1 ? 's' : ''}</span>
                )}
                {activeSub && <Badge color="green">Copying</Badge>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {activeSub ? (
              <>
                <Link to={`/bots/${activeSub.copierBot?.id}`}>
                  <Button size="sm">View Bot</Button>
                </Link>
                {!showStopConfirm && (
                  <Button variant="danger" size="sm" onClick={() => setShowStopConfirm(true)}>
                    Stop
                  </Button>
                )}
              </>
            ) : signal.is_visible ? (
              <Button onClick={() => setShowCopyModal(true)} size="sm">
                <Copy size={14} className="mr-1" /> Copy
              </Button>
            ) : null}

            {isAdmin && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={updateSignal.isPending}
                  onClick={() => {
                    if (signal.is_visible) {
                      // Unpublishing affects users — confirm first
                      setShowUnpublishConfirm(true);
                    } else {
                      updateSignal.mutate({
                        id: signal.id,
                        data: { is_visible: true, is_active: true } as any,
                      });
                    }
                  }}
                >
                  {signal.is_visible ? <><EyeOff size={12} className="mr-1" /> Unpublish</> : <><Eye size={12} className="mr-1" /> Publish</>}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  <Trash2 size={12} className="mr-1" /> Delete
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-border">
        {([
          { key: 'overview', label: 'Overview', icon: BarChart3 },
          { key: 'positions', label: 'Positions', icon: Layers },
          { key: 'history', label: 'Trading History', icon: Clock },
        ] as const).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold transition-colors cursor-pointer border-b-2 -mb-px ${
              tab === t.key ? 'border-orange text-orange' : 'border-transparent text-ink-3 hover:text-ink'
            }`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'overview' && (
        <OverviewTab
          signal={signal}
          performance={performance}
          perfPeriod={perfPeriod}
          setPerfPeriod={setPerfPeriod}
          currentPerf={currentPerf}
          dailyPnl={dailyPnl}
          copierStats={copierStats}
          symbolDisplay={symbolDisplay}
          subscriptions={(subscriptions ?? []).filter(s => s.signal?.id === id)}
        />
      )}
      {tab === 'positions' && <PositionsTab signalId={id!} signal={signal} livePrices={livePrices} />}
      {tab === 'history' && <HistoryTab signalId={id!} signal={signal} livePrices={livePrices} />}

      <CopyBotDrawer open={showCopyModal} signal={signal} onClose={() => setShowCopyModal(false)} />

      {/* Unpublish confirmation modal */}
      <Modal
        open={showUnpublishConfirm}
        onClose={() => !updateSignal.isPending && setShowUnpublishConfirm(false)}
        title="Unpublish Signal"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowUnpublishConfirm(false)} disabled={updateSignal.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={updateSignal.isPending}
              onClick={() => {
                updateSignal.mutate(
                  {
                    id: signal.id,
                    data: { is_visible: false, is_active: false } as any,
                  },
                  { onSuccess: () => setShowUnpublishConfirm(false) },
                );
              }}
            >
              <EyeOff size={12} className="mr-1" />
              {updateSignal.isPending ? 'Unpublishing...' : 'Unpublish Signal'}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold mb-1">Unpublish "{signal.name}"?</p>
            <p className="text-xs text-ink-3 leading-relaxed">
              Users will no longer see this signal in Discover and won't be able to copy it.
              Existing copier bots will keep running until manually stopped. You can publish
              it again later.
            </p>
          </div>
        </div>
      </Modal>

      {/* Delete signal confirmation modal */}
      <Modal
        open={showDeleteConfirm}
        onClose={() => !deleteSignal.isPending && setShowDeleteConfirm(false)}
        title="Delete Signal"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowDeleteConfirm(false)} disabled={deleteSignal.isPending}>
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              disabled={deleteSignal.isPending}
              onClick={() => {
                deleteSignal.mutate(signal.id, {
                  onSuccess: () => {
                    setShowDeleteConfirm(false);
                    navigate('/copy-trade/admin');
                  },
                });
              }}
            >
              <Trash2 size={12} className="mr-1" />
              {deleteSignal.isPending ? 'Deleting...' : 'Delete Signal'}
            </Button>
          </>
        }
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-red flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold mb-1">Delete "{signal.name}"?</p>
            <p className="text-xs text-ink-3 leading-relaxed">
              This will permanently remove the signal AND clean up all copiers:
              all open positions on copier bots will be closed at market, the bots
              will be archived, and capital returned. This action cannot be undone.
            </p>
          </div>
        </div>
      </Modal>

      {/* Stop confirmation modal */}
      <Modal
        open={showStopConfirm}
        onClose={() => setShowStopConfirm(false)}
        title="Stop Copying"
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setShowStopConfirm(false)}>Cancel</Button>
            <Button variant="danger" size="sm" disabled={unsubscribe.isPending}
              onClick={() => {
                if (activeSub) {
                  unsubscribe.mutate(activeSub.id, { onSuccess: () => setShowStopConfirm(false) });
                }
              }}>
              {unsubscribe.isPending ? 'Stopping...' : 'Stop Copying'}
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <p className="text-sm font-semibold">Are you sure?</p>
          <p className="text-xs text-ink-3 leading-relaxed">
            This will close all open positions at market price and archive the copy bot. This action cannot be undone.
          </p>
        </div>
      </Modal>
    </div>
  );
}

// ─── OVERVIEW TAB ─────────────────────────────────────────────────────

function OverviewTab({ signal, performance: _performance, perfPeriod, setPerfPeriod, currentPerf, dailyPnl, copierStats, symbolDisplay, subscriptions: _subscriptions }: {
  signal: CopySignal;
  performance: Record<string, PeriodPerformance> | undefined;
  perfPeriod: PerfPeriod;
  setPerfPeriod: (p: PerfPeriod) => void;
  currentPerf: PeriodPerformance | undefined;
  dailyPnl: any[] | undefined;
  copierStats: any;
  symbolDisplay: string;
  subscriptions: CopySubscription[];
}) {
  return (
    <div className="space-y-5">
      {/* Description */}
      {signal.description && (
        <div className="glass-sm rounded-lg p-4">
          <p className="text-sm text-ink-3 whitespace-pre-line leading-relaxed">{signal.description}</p>
        </div>
      )}
      {/* Performance overview */}
      <div className="glass-sm rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-ink flex items-center gap-1.5">
            <TrendingUp size={14} className="text-orange" /> Performance
          </h3>
          <div className="flex gap-1">
            {(['7D', '30D', '90D', '180D', '360D', 'ALL'] as PerfPeriod[]).map(p => (
              <button key={p} onClick={() => setPerfPeriod(p)}
                className={`px-2 py-0.5 text-[10px] font-semibold rounded-full transition-colors cursor-pointer ${
                  perfPeriod === p ? 'bg-orange text-white' : 'bg-surface text-ink-3 hover:text-ink'
                }`}>{p}</button>
            ))}
          </div>
        </div>

        {currentPerf ? (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div>
              <p className="text-[10px] text-ink-4">P&L</p>
              <p className={`text-lg font-bold font-mono ${pnlColor(currentPerf.pnl)}`}>{formatPnl(currentPerf.pnl)}</p>
            </div>
            <div>
              <p className="text-[10px] text-ink-4">Trades</p>
              <p className="text-lg font-bold font-mono text-ink">{currentPerf.trades}</p>
            </div>
            <div>
              <p className="text-[10px] text-ink-4">Win Rate</p>
              <p className="text-lg font-bold font-mono text-ink">{currentPerf.winRate}%</p>
            </div>
            <div>
              <p className="text-[10px] text-ink-4">Wins</p>
              <p className="text-lg font-bold font-mono text-green">{currentPerf.wins}</p>
            </div>
            <div>
              <p className="text-[10px] text-ink-4">Losses</p>
              <p className="text-lg font-bold font-mono text-red">{currentPerf.losses}</p>
            </div>
          </div>
        ) : (
          <p className="text-xs text-ink-4">Loading performance data...</p>
        )}
      </div>

      {/* P&L Chart */}
      <div className="glass-sm rounded-lg p-4">
        <h3 className="text-sm font-bold text-ink mb-3 flex items-center gap-1.5">
          <BarChart3 size={14} className="text-orange" /> Cumulative P&L (90D)
        </h3>
        <div className="h-40">
          {dailyPnl && dailyPnl.length > 1 ? (
            <PnlChart data={dailyPnl} />
          ) : (
            <div className="h-full flex items-center justify-center">
              <p className="text-xs text-ink-4">Collecting trade data...</p>
            </div>
          )}
        </div>
      </div>

      {/* Copier & Assets stats */}
      {copierStats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="glass-sm rounded-lg p-4">
            <p className="text-[10px] text-ink-4 uppercase tracking-wider mb-1">Total Copiers</p>
            <p className="text-2xl font-bold font-mono text-ink">{copierStats.totalCopiers}</p>
            <p className="text-[10px] text-ink-4 mt-0.5">{copierStats.activeCopiers} active</p>
          </div>
          <div className="glass-sm rounded-lg p-4">
            <p className="text-[10px] text-ink-4 uppercase tracking-wider mb-1">Total Copy Assets</p>
            <p className="text-2xl font-bold font-mono text-ink">${copierStats.totalAssets.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-ink-4 mt-0.5">across active subscriptions</p>
          </div>
          <div className="glass-sm rounded-lg p-4">
            <p className="text-[10px] text-ink-4 uppercase tracking-wider mb-1">Avg Capital per Copier</p>
            <p className="text-2xl font-bold font-mono text-ink">
              {copierStats.activeCopiers > 0
                ? `$${(copierStats.totalAssets / copierStats.activeCopiers).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
                : '—'}
            </p>
          </div>
        </div>
      )}

      {/* Strategy Info */}
      <div className="glass-sm rounded-lg p-4">
        <h3 className="text-sm font-bold text-ink mb-3 flex items-center gap-1.5">
          <Info size={14} className="text-orange" /> Strategy Info
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <p className="text-[10px] text-ink-4">Symbol</p>
            <p className="font-semibold text-ink">{symbolDisplay}</p>
          </div>
          <div>
            <p className="text-[10px] text-ink-4">Direction</p>
            <p className="font-semibold text-ink capitalize">{signal.direction_filter}</p>
          </div>
          {signal.default_lot_size && (
            <div>
              <p className="text-[10px] text-ink-4">Recommended Lot</p>
              <p className="font-semibold text-ink">{signal.default_lot_size}</p>
            </div>
          )}
          {signal.default_sl_pct && (
            <div>
              <p className="text-[10px] text-ink-4">Recommended SL</p>
              <p className="font-semibold text-ink">{signal.default_sl_pct}%</p>
            </div>
          )}
          <div>
            <p className="text-[10px] text-ink-4">Created</p>
            <p className="font-semibold text-ink">{new Date(signal.created_at).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-[10px] text-ink-4">Status</p>
            <p className="font-semibold text-ink">{signal.is_verified ? 'Verified' : 'Unverified'}</p>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── POSITIONS TAB ────────────────────────────────────────────────────

function PositionsTab({ signalId, signal, livePrices }: { signalId: string; signal: CopySignal; livePrices?: Record<string, { ask: number; bid: number }> }) {
  const { data: tradesData } = useSignalTrades(signalId, { status: 'OPEN', limit: 100, refetchInterval: 5_000 });
  const trades = tradesData?.trades ?? [];

  if (trades.length === 0) {
    return <div className="text-center py-12"><p className="text-xs text-ink-4">No open positions</p></div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-ink-4">
            <th className="text-left px-4 py-3 font-medium">Symbol</th>
            <th className="text-left px-4 py-3 font-medium">Type</th>
            <th className="text-right px-4 py-3 font-medium">Volume</th>
            <th className="text-right px-4 py-3 font-medium">Open</th>
            <th className="text-right px-4 py-3 font-medium">Current</th>
            <th className="text-right px-4 py-3 font-medium">P&L</th>
            <th className="text-left px-4 py-3 font-medium">Time</th>
          </tr>
        </thead>
        <tbody>
          {trades.map(t => <TradeRow key={t.id} trade={t} accountType={signal.account_type} livePrices={livePrices} />)}
        </tbody>
      </table>
    </div>
  );
}

// ─── HISTORY TAB ──────────────────────────────────────────────────────

function HistoryTab({ signalId, signal, livePrices }: { signalId: string; signal: CopySignal; livePrices?: Record<string, { ask: number; bid: number }> }) {
  const [page, setPage] = useState(1);
  const { data: tradesData } = useSignalTrades(signalId, { status: 'CLOSED', limit: 20, page, refetchInterval: 10_000 });
  const trades = tradesData?.trades ?? [];
  const totalPages = Math.ceil((tradesData?.total ?? 0) / 20);

  if (trades.length === 0) {
    return <div className="text-center py-12"><p className="text-xs text-ink-4">No closed trades yet</p></div>;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-ink-4">
              <th className="text-left px-4 py-3 font-medium">Symbol</th>
              <th className="text-left px-4 py-3 font-medium">Type</th>
              <th className="text-right px-4 py-3 font-medium">Volume</th>
              <th className="text-right px-4 py-3 font-medium">Open</th>
              <th className="text-right px-4 py-3 font-medium">Close</th>
              <th className="text-right px-4 py-3 font-medium">P&L</th>
              <th className="text-left px-4 py-3 font-medium">Date</th>
            </tr>
          </thead>
          <tbody>
            {trades.map(t => <TradeRow key={t.id} trade={t} accountType={signal.account_type} livePrices={livePrices} />)}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Prev</Button>
          <span className="text-xs text-ink-3">{page} / {totalPages}</span>
          <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
        </div>
      )}
    </div>
  );
}

// ─── SHARED COMPONENTS ────────────────────────────────────────────────

export function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string; color?: string }) {
  return (
    <div className="glass-sm rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={12} className="text-ink-4" />
        <span className="text-[10px] text-ink-4 uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-lg font-bold font-mono ${color || 'text-ink'}`}>{value}</p>
    </div>
  );
}

function normalizeVolume(volume: number, accountType?: 'STANDARD' | 'CENT' | 'MINI' | 'NANO'): number {
  if (accountType === 'CENT') return volume / 100;
  if (accountType === 'MINI') return volume * 10;
  if (accountType === 'NANO') return volume * 100;
  return volume;
}

function TradeRow({ trade, accountType, livePrices }: { trade: ProviderTrade; accountType?: 'STANDARD' | 'CENT' | 'MINI' | 'NANO'; livePrices?: Record<string, { ask: number; bid: number }> }) {
  const date = new Date(trade.opened_at);
  const displayVolume = normalizeVolume(trade.volume, accountType);
  let displayPnl = trade.gross_pnl;
  let currentPrice: number | null = trade.close_price;

  if (trade.status === 'OPEN' && livePrices) {
    const price = livePrices[trade.symbol];
    if (price) {
      currentPrice = trade.type === 'BUY' ? price.bid : price.ask;
      const diff = trade.type === 'BUY' ? currentPrice - trade.open_price : trade.open_price - currentPrice;
      // P&L computed from the normalized (display) volume so it matches what the user sees
      displayPnl = parseFloat((diff * displayVolume * getClientContractSize(trade.symbol, livePrices || undefined)).toFixed(2));
    }
  }

  return (
    <tr className="border-b border-border hover:bg-surface/50 transition-colors">
      <td className="px-4 py-3.5 font-semibold text-ink">{trade.symbol}</td>
      <td className="px-4 py-3.5"><Badge color={trade.type === 'BUY' ? 'green' : 'red'}>{trade.type === 'BUY' ? 'Buy' : 'Sell'}</Badge></td>
      <td className="px-4 py-3.5 text-right font-mono text-ink-3">{displayVolume < 0.01 ? displayVolume.toFixed(4) : displayVolume.toFixed(2)}</td>
      <td className="px-4 py-3.5 text-right font-mono text-ink-3">{formatPrice(trade.open_price, trade.symbol)}</td>
      <td className="px-4 py-3.5 text-right font-mono text-ink-3">
        {currentPrice != null ? formatPrice(currentPrice, trade.symbol) : '—'}
      </td>
      <td className={`px-4 py-3.5 text-right font-mono font-semibold ${pnlColor(displayPnl)}`}>
        {displayPnl !== 0 ? formatPnl(displayPnl) : '—'}
      </td>
      <td className="px-4 py-3.5 text-ink-4">
        {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </td>
    </tr>
  );
}

function PnlChart({ data }: { data: { date: string; pnl: number; cumulative: number }[] }) {
  if (data.length < 2) return null;

  const values = data.map(d => d.cumulative);
  const min = Math.min(...values, 0);
  const max = Math.max(...values, 0);
  const range = max - min || 1;
  const w = 400;
  const h = 160;
  const pad = 4;

  // Zero line position
  const zeroY = h - pad - ((0 - min) / range) * (h - pad * 2);

  const points = data.map((d, i) => {
    const x = pad + (i / (data.length - 1)) * (w - pad * 2);
    const y = h - pad - ((d.cumulative - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  }).join(' ');

  const lastVal = values[values.length - 1];
  const color = (lastVal ?? 0) >= 0 ? '#059669' : '#dc2626';
  const firstX = pad;
  const lastX = pad + ((data.length - 1) / (data.length - 1)) * (w - pad * 2);
  const areaPoints = `${firstX},${zeroY} ${points} ${lastX},${zeroY}`;

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full" preserveAspectRatio="none">
      {/* Zero line */}
      <line x1={pad} y1={zeroY} x2={w - pad} y2={zeroY} stroke="var(--theme-ink-4)" strokeWidth="0.5" strokeDasharray="4,4" opacity="0.3" />
      {/* Area */}
      <polygon points={areaPoints} fill={color} opacity="0.1" />
      {/* Line */}
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
    </svg>
  );
}

